import {setCurrentModal} from '~/shared/store/storeModules/currentModal/currentModalActions';
import store from '~/shared/store';
import {MoneyCard} from '~/shared/services/bffService/api/interfaces/responses';
import {replaceRoute} from '~/shared/router';
import {createLogger} from '~/shared/logging';
import {getLocalizationService} from '~/shared/services/localisationService';
import {cancelMoneycard} from '~/shared/services/bffService/api';
import AddCreditCard from '~/shared/components/AddCreditCard/AddCreditCard';
import {MoneycardOtlRuleManagement} from '~/shared/store/models';
import {is401Error} from '~/shared/services/apiErrorService';
import {handleRefreshToken} from '~/shared/services/auth';
import {trackEvent} from '~/shared/services/analytics';

import cancel10bisCard from './assets/cancel10bisCard.png';
import frustratedImg from './assets/frustratedImg.png';
import goodByeImage from './assets/goodByeImage.svg';
import removeMoneycardErrorImage from './assets/removeMoneycardErrorImage.svg';
import changeConfirmSrc from './assets/changeConfirm.png';

const logger = createLogger('MyTenbisActions');

export const linkCreditCardHandler = (
  currentMoneycard: MoneyCard,
  otlData: MoneycardOtlRuleManagement | null,
  fetchOtl: () => Promise<void>,
) => {
  trackEvent('linkCC2MC_click');
  const {t} = getLocalizationService();

  const getFailureModalObject = (headerKey: string) => ({
    imgSrc: frustratedImg,
    imgAlt: t('failure_picture'),
    header: t(headerKey),
    subHeader: t('try_again_later_2'),
    primaryButton: {
      label: t('close_2'),
      onClick: () => store.dispatch(setCurrentModal(null)),
    },
  });
  const getFailureModal = (headerKey: string) => () => {
    store.dispatch(setCurrentModal('moneycardFeedbackModal', getFailureModalObject(headerKey)));
  };
  const openOtlAction = () => {
    store.dispatch(setCurrentModal('otlModal', {
      showBackArrow: false,
      showUnlinkButton: true,
      onSuccess: () => {
        fetchOtl();
        store.dispatch(setCurrentModal('moneycardFeedbackModal', {
          imgSrc: changeConfirmSrc,
          imgAlt: t('success'),
          header: t('great_your_changes_were_saved'),
          primaryButton: {
            label: t('close_2'),
            onClick: () => store.dispatch(setCurrentModal(null)),
          },
        }));
      },
      onFailure: () =>
        store.dispatch(setCurrentModal('moneycardFeedbackModal', getFailureModalObject('the_changes_were_not_saved'))),
    }));
  };
  const getLinkCreditcardModal = ({addCreditCardAction: addCCAction, hasSuccessfullyAddedCreditCard}: {
    addCreditCardAction: () => void;
    hasSuccessfullyAddedCreditCard?: boolean;
  }) => () => {
    store.dispatch(setCurrentModal('linkMoneycardModal', {
      currentMoneycard,
      otlData,
      addCreditCardAction: addCCAction,
      hasSuccessfullyAddedCreditCard,
      onSuccess: fetchOtl,
      onLinkFailed: getFailureModal('we_couldnt_link_the_credit_card_to_your_10bis'),
      openOtlAction,
    }));
  };

  const addCreditCardAction = () =>
    store.dispatch(setCurrentModal('addPaymentMethodModal', {
      AddCreditCardComponent: AddCreditCard,
      onSuccess: () => {
        trackEvent('hasSuccessfullyAddedCreditCard');
        getLinkCreditcardModal({
          addCreditCardAction,
          hasSuccessfullyAddedCreditCard: true,
        })();
      },
      onFailure: getFailureModal('credit_card_addition_failed'),
    }));

  if (otlData?.otlRule.ruleActive && otlData?.otlRule.creditcardId) {
    openOtlAction();
    return;
  }

  if (!otlData?.userCreditcards || !otlData?.userCreditcards.length) {
    addCreditCardAction();
    return;
  }
  getLinkCreditcardModal({addCreditCardAction})();
};

export const manageBillingPermissions = (currentMoneycard: MoneyCard) => {
  trackEvent('billingPermissions_click');
  store.dispatch(setCurrentModal('billingPermissions', {moneycardId: currentMoneycard.id}));
};

export const moneycardLostOrStolen = (currentMoneycard: MoneyCard) => {
  trackEvent('mcLost_click');
  store.dispatch(setCurrentModal('reIssueOrReplaceMoneycard', {moneycardId: currentMoneycard.id, isReIssue: false}));
};

export const moneycardWornOrNotWorking = (currentMoneycard: MoneyCard) => {
  trackEvent('mcWorn_click');
  store.dispatch(setCurrentModal('reIssueOrReplaceMoneycard', {moneycardId: currentMoneycard.id, isReIssue: true}));
};

export const cancelMoneycardHandler = async (currentMoneycard: MoneyCard, refetchPayments: () => Promise<void>) => {
  trackEvent('mcCancel_click');
  if (!currentMoneycard) {
    logger.warn('No current moneycard found, cannot cancel moneycard');
    return;
  }

  const {t} = getLocalizationService();

  const cancelHandler = async () => {
    trackEvent('mcCancel_submit');
    try {
      await cancelMoneycard(currentMoneycard.id);
      store.dispatch(setCurrentModal('cancelMoneycard', {
        imgSrc: goodByeImage,
        header: t('moneycard_xxxx_cancelled_successfully', {fourDigits: currentMoneycard.cardLastDigits}),
        primaryButton: {
          label: t('close_2'),
          onClick: () => {
            store.dispatch(setCurrentModal(null));
            replaceRoute('/personal-area', {keepAllQueries: false});
            refetchPayments();
          },
        },
      }));
    } catch (error) {
      if (is401Error(error)) {
        handleRefreshToken(error, cancelHandler);
        return;
      }

      store.dispatch(setCurrentModal('cancelMoneycard', {
        imgSrc: removeMoneycardErrorImage,
        header: t('moneycard_cancel_failed_title'),
        subHeader: t('moneycard_cancel_error_description'),
        primaryButton: {
          label: t('close_2'),
          onClick: () => {
            store.dispatch(setCurrentModal(null));
          },
        },
      }));
    }
  };

  store.dispatch(setCurrentModal('cancelMoneycard', {
    imgSrc: cancel10bisCard,
    header: `${t('cancel_10bis_card')} ${currentMoneycard.cardLastDigits}`,
    subHeader: t('cancellation_is_final_and_irreversible'),
    primaryButton: {
      label: t('confirm_cancel_card'),
      onClick: cancelHandler,
    },
    secondaryButton: {
      label: t('cancel_card_decline'),
      onClick: () => {
        store.dispatch(setCurrentModal(null));
      },
    },
  }));
};
